/* Consent-controlled Kunnie storefront analytics. Contract and event list: docs/posthog.md. */
(() => {
  const element = document.getElementById('kunnie-analytics-config');
  if (!element || window.kunnieAnalytics) return;
  let config;
  try {
    config = JSON.parse(element.textContent);
  } catch (error) {
    return;
  }
  const token = String(config.token || '').trim();
  const posthogOn = /^phc_[A-Za-z0-9]+$/.test(token);
  if (!posthogOn) return;

  const QUEUE_LIMIT = 50;
  const PRIVATE_KEY = /e-?mail|phone|password|token|address|post_?code|postal|zip|first_?name|last_?name|full_?name|customer_?id/i;
  const EMAIL = /[^\s@/?&=]+@[^\s@/?&=]+\.[a-z]{2,}/gi;
  const PRIVATE_SELECTOR = '[data-kunnie-private]';
  let state = 'pending';
  let consentVersion = 0;
  let started = false;
  let ready = false;
  let queue = [];

  function clean(value, depth = 0) {
    if (Array.isArray(value)) return value.slice(0, 50).map((entry) => clean(entry, depth + 1));
    if (value && typeof value === 'object') {
      if (depth > 3) return undefined;
      const result = {};
      for (const [key, entry] of Object.entries(value)) {
        if (PRIVATE_KEY.test(key)) continue;
        const cleaned = clean(entry, depth + 1);
        if (cleaned !== undefined) result[key] = cleaned;
      }
      return result;
    }
    if (typeof value === 'string') return value.replace(EMAIL, '[email]').slice(0, 500);
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    return typeof value === 'boolean' || value === null ? value : undefined;
  }

  function capture(event, properties) {
    window.posthog.capture(event, properties);
  }

  function track(event, properties) {
    if (typeof event !== 'string' || !/^[a-z][a-z0-9_]{1,63}$/.test(event) || state !== 'granted') return false;
    const payload = clean(properties || {}) || {};
    if (!posthogOn) return false;
    if (state === 'granted' && ready) capture(event, payload);
    else if (queue.length < QUEUE_LIMIT) queue.push([event, payload]);
    return true;
  }

  function superProperties() {
    const theme = (window.Shopify && window.Shopify.theme) || {};
    return Object.assign({}, clean(config.context || {}), { theme_id: theme.id, theme_role: theme.role });
  }

  function redactUrls(event) {
    if (state !== 'granted') return null;
    if (!event || !event.properties) return event;
    const urls = new Set(['$current_url', '$referrer', '$initial_current_url', '$initial_referrer']);
    function redact(value, key) {
      if (typeof value === 'string') {
        if (urls.has(key)) {
          try {
            const url = new URL(value);
            for (const name of [...url.searchParams.keys()]) {
              if (!/^utm_[a-z_]+$/i.test(name)) url.searchParams.delete(name);
            }
            url.hash = '';
            value = url.href;
          } catch { return undefined; }
        }
        return value.replace(EMAIL, '[email]');
      }
      if (Array.isArray(value)) return value.map((entry) => redact(entry));
      if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([name, entry]) => [name, redact(entry, name)]));
      }
      return value;
    }
    event.properties = redact(event.properties);
    return event;
  }

  // Shopify renders 404 pages at /404, so the requested path and the linking page come from the browser.
  function pageProperties() {
    const properties = Object.assign({}, config.page.properties);
    if (config.page.event === 'page_not_found') {
      properties.path = location.pathname;
      try {
        const referrer = document.referrer ? new URL(document.referrer) : null;
        if (referrer) properties[referrer.host === location.host ? 'referrer_path' : 'referrer_host'] = referrer.host === location.host ? referrer.pathname : referrer.host;
      } catch (error) {
        /* no usable referrer */
      }
    }
    return properties;
  }

  function activate(instance) {
    if (state !== 'granted') return;
    instance.opt_in_capturing({ captureEventName: false });
    instance.register(superProperties());
    if (config.replay === true) instance.startSessionRecording();
    instance.capture('$pageview');
    if (config.page && config.page.event) capture(config.page.event, clean(pageProperties()) || {});
    if (new URL(location.href).searchParams.get('customer_posted') === 'true') track('newsletter_signup_accepted', { source: 'native_form_return' });
    const pending = queue;
    queue = [];
    pending.forEach(([event, properties]) => capture(event, properties));
  }

  function onLoaded(instance) {
    ready = true;
    if (state !== 'granted') {
      instance.stopSessionRecording();
      instance.opt_out_capturing();
      forgetStorage();
      return;
    }
    activate(instance);
  }

  // Official PostHog web snippet (posthog.com/docs/libraries/js): a call-queue stub that loads array.js.
  function stub() {
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],Object.defineProperty(u,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e}}),Object.defineProperty(u.people,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(){return u.toString(1)+".people (stub)"}}),o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagResult isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  }

  function start() {
    started = true;
    stub();
    window.posthog.init(token, {
      api_host: config.apiHost || 'https://eu.i.posthog.com',
      ui_host: config.uiHost || 'https://eu.posthog.com',
      defaults: '2026-05-30',
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: false,
      opt_out_capturing_by_default: true,
      capture_pageview: false,
      person_profiles: 'identified_only',
      opt_out_persistence_by_default: true,
      capture_pageleave: true,
      capture_dead_clicks: true,
      capture_heatmaps: config.heatmaps === true,
      capture_exceptions: true,
      ip: false,
      capture_performance: { web_vitals: true, network_timing: true },
      disable_session_recording: config.replay !== true,
      enable_recording_console_log: false,
      session_recording: { maskAllInputs: true, maskTextSelector: PRIVATE_SELECTOR, blockSelector: PRIVATE_SELECTOR },
      mask_personal_data_properties: true,
      custom_personal_data_properties: ['email', 'key', 'token', 'checkout_token', 'phone'],
      autocapture: { css_selector_ignorelist: ['.ph-no-autocapture', '[data-ph-no-autocapture]', PRIVATE_SELECTOR] },
      before_send: redactUrls,
      loaded: onLoaded,
    });
  }

  // Without consent no PostHog identifier may stay on the device, including one from an earlier visit.
  // Once PostHog runs on this page its opt-out marker stays: it is what keeps the loaded instance silent.
  function forgetStorage() {
    const prefix = 'ph_' + token;
    const optOutKey = '__ph_opt_in_out_' + token;
    const owned = (name) => name.startsWith(prefix) || (!started && name === optOutKey);
    for (const store of ['localStorage', 'sessionStorage']) {
      try {
        Object.keys(window[store]).filter(owned).forEach((key) => window[store].removeItem(key));
      } catch (error) {
        /* storage unavailable */
      }
    }
    const parts = location.hostname.split('.');
    const domains = [''];
    for (let i = 0; i < parts.length - 1; i += 1) domains.push('; domain=.' + parts.slice(i).join('.'));
    document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter(owned)
      .forEach((name) => {
        domains.forEach((domain) => {
          document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + domain;
        });
      });
  }

  function grant() {
    if (state === 'granted') return;
    consentVersion += 1;
    state = 'granted';
    if (started && ready) activate(window.posthog);
    else if (!started) start();
  }

  function deny(decided) {
    if (state === 'granted') consentVersion += 1;
    state = decided ? 'denied' : 'pending';
    queue = [];
    for (const entry of settling.values()) clearTimeout(entry.timer);
    settling.clear();
    if (!posthogOn) return;
    if (started) {
      window.posthog.stopSessionRecording();
      window.posthog.opt_out_capturing();
    }
    forgetStorage();
  }

  function apply(allowed, decided) {
    if (allowed) grant();
    else deny(decided || state === 'granted');
  }

  function readPrivacy() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return null;
    let allowed = false;
    let decision = '';
    try {
      allowed = privacy.analyticsProcessingAllowed() === true;
      decision = (privacy.currentVisitorConsent() || {}).analytics || '';
      allowed = allowed && decision === 'yes';
    } catch (error) {
      /* treat as not allowed */
    }
    return { allowed, declined: decision === 'no' };
  }

  function evaluate() {
    const result = readPrivacy();
    if (result) apply(result.allowed, result.declined);
  }

  // Typing states (search fields) are reported once they settle for 1.5 s, not per keystroke.
  const settling = new Map();
  function settle(key, event, properties) {
    if (state !== 'granted') return;
    const pending = settling.get(key);
    if (pending) clearTimeout(pending.timer);
    settling.set(key, { event, properties, timer: setTimeout(() => flush(key), 1500) });
  }
  function flush(key) {
    for (const [name, entry] of settling) {
      if (key && name !== key) continue;
      clearTimeout(entry.timer);
      settling.delete(name);
      track(entry.event, entry.properties);
    }
  }

  const toId = (value) => {
    const id = String(value === undefined || value === null ? '' : value).split('/').pop();
    return /^\d+$/.test(id) ? Number(id) : id || undefined;
  };
  const amount = (value) => (value === undefined || value === null || value === '' ? undefined : Number(value));
  const cart = { value: amount(config.cart && config.cart.value), items: config.cart && config.cart.items_count };

  function summarize(summary) {
    if (!summary) return {};
    const total = summary.cost && summary.cost.totalAmount;
    return { cart_value: amount(total && total.amount), cart_items_count: summary.totalQuantity, currency: total && total.currencyCode };
  }

  // Remembers the basket total and reports when it first passes the free-delivery threshold.
  function updateCart(summary) {
    if (!summary || summary.cart_value === undefined) return;
    const threshold = Number(config.freeShippingThreshold) || 0;
    if (threshold > 0 && cart.value !== undefined && cart.value < threshold && summary.cart_value >= threshold) {
      track('free_shipping_threshold_reached', { cart_value: summary.cart_value, threshold });
    }
    cart.value = summary.cart_value;
    cart.items = summary.cart_items_count;
  }

  function addSource(target) {
    if (!(target instanceof Element)) return 'other';
    if (target.closest('kunnie-product-pairing')) return 'pdp_match';
    if (target.closest('kunnie-bundle-builder')) return 'bundle_builder';
    if (target.closest('kunnie-cart-upsell')) return 'cart_upsell';
    if (target.closest('.kunnie-look__dialog')) return 'shop_the_look';
    if (target.closest('kunnie-wishlist-drawer')) return 'wishlist';
    if (target.closest('kunnie-cart-empty')) return 'empty_cart';
    if (target.closest('quick-add-dialog')) return 'quick_add';
    if (target.closest('kunnie-add-button')) return 'product_card';
    if (target.closest('product-form-component')) return target.querySelector('[data-puppet="true"]') ? 'sticky_bar' : 'product_page';
    return 'other';
  }

  function cartRow(root, key) {
    const row = root instanceof Element ? [...root.querySelectorAll('[data-key]')].find((entry) => entry.dataset.key === key) : null;
    const text = (selector) => {
      const node = row && row.querySelector(selector);
      return (node && node.textContent.trim()) || undefined;
    };
    const input = row && row.querySelector('input[type="number"]');
    return {
      variant_id: toId(String(key || '').split(':')[0]),
      product_title: text('.cart-items__title'),
      brand: text('.cart-items__vendor'),
      previous_quantity: input ? amount(input.defaultValue) : undefined,
    };
  }

  function describeFilters(filters) {
    return (filters || []).slice(0, 20).map((filter) => {
      if (filter.productVendor) return 'brand:' + filter.productVendor;
      if (filter.productType) return 'type:' + filter.productType;
      if (filter.variantOption) return 'option:' + filter.variantOption.name + '=' + (filter.variantOption.value || '');
      if (filter.price) return 'price:' + (filter.price.min === undefined ? '' : filter.price.min) + '-' + (filter.price.max === undefined ? '' : filter.price.max);
      if (filter.available !== undefined) return 'available:' + filter.available;
      if (filter.tag) return 'tag:' + filter.tag;
      if (filter.category) return 'category:' + toId(filter.category.id);
      const field = filter.productMetafield || filter.variantMetafield || filter.taxonomyMetafield;
      return field ? 'metafield:' + (field.namespace ? field.namespace + '.' : '') + field.key + '=' + (field.value || '') : 'other';
    });
  }

  function dataProperties(element) {
    const properties = {};
    for (const [key, value] of Object.entries(element.dataset)) {
      if (key.length > 11 && key.startsWith('kunnieTrack')) {
        properties[key.slice(11).replace(/[A-Z]/g, (letter, index) => (index ? '_' : '') + letter.toLowerCase())] = value;
      }
    }
    return properties;
  }

  function resultType(href) {
    const path = new URL(href, location.href).pathname;
    if (/\/products\//.test(path)) return 'product';
    if (/\/collections\//.test(path)) return 'collection';
    if (/\/blogs\//.test(path)) return 'recipe';
    if (/\/pages\//.test(path)) return 'page';
    return /\/search/.test(path) ? 'all_results' : 'other';
  }

  window.kunnieAnalytics = { track };
  document.addEventListener('kunnie:track', (event) => {
    if (state !== 'granted') return;
    const detail = event.detail || {};
    if (detail.settle) settle(detail.settle, detail.event, detail.properties);
    else {
      flush();
      track(detail.event, detail.properties);
    }
  });
  addEventListener('pagehide', () => flush());

  // Horizon reports cart, search, filter and variant changes as Shopify storefront events with a result promise.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (state !== 'granted') return;
    const version = consentVersion;
    const lines = event.lines || [];
    const root = event.target;
    const surface = root instanceof Element && root.closest('#cart-drawer') ? 'drawer' : 'page';
    const source = event.action === 'add' ? addSource(root) : undefined;
    const rows = event.action === 'add' ? [] : lines.map((line) => cartRow(root, line.id));
    if (!event.promise) return;
    event.promise
      .then((result) => {
        if (state !== 'granted' || version !== consentVersion) return;
        const detail = (result && result.detail) || {};
        if (detail.didError) return;
        const summary = summarize(result && result.cart);
        if (detail.source !== 'kunnie-bundle-builder') {
          lines.forEach((line, index) => {
            if (event.action === 'add') {
              const variantId = toId(line.merchandiseId);
              const item = (detail.items || []).find((entry) => toId(entry.variant_id || entry.id) === variantId) || {};
              const price = item.price === undefined ? undefined : Number(item.price) / 100;
              track('product_added_to_cart', Object.assign({
                source, product_id: item.product_id, variant_id: variantId, product_title: item.product_title, variant_title: item.variant_title,
                brand: item.vendor, product_type: item.product_type, sku: item.sku, price, quantity: line.quantity,
                value: price === undefined ? undefined : Math.round(price * line.quantity * 100) / 100,
              }, summary));
            } else {
              track(line.quantity > 0 ? 'cart_quantity_changed' : 'product_removed_from_cart', Object.assign({ surface, quantity: line.quantity }, rows[index], summary));
            }
          });
        }
        updateCart(summary);
      })
      .catch(() => {});
  });
  document.addEventListener('shopify:cart:error', (event) => {
    if (state !== 'granted') return;
    track('cart_error', { code: event.code, message: String(event.error || '').slice(0, 160) });
  });
  document.addEventListener('shopify:cart:view', (event) => {
    if (state !== 'granted') return;
    const summary = summarize(event.cart);
    track('cart_viewed', Object.assign({ surface: event.context === 'dialog' ? 'drawer' : 'page' }, summary));
    updateCart(summary);
  });
  document.addEventListener('shopify:product:view', (event) => {
    if (state !== 'granted') return;
    if (event.context !== 'dialog' || !event.product) return;
    const variant = event.product.selectedVariant || {};
    track('quick_view_opened', {
      product_id: toId(event.product.id), product_handle: event.product.handle, product_title: event.product.title,
      brand: event.product.vendor, variant_id: toId(variant.id), sku: variant.sku || undefined,
      price: amount(variant.price && variant.price.amount), available: variant.availableForSale,
    });
  });
  document.addEventListener('shopify:product:select', (event) => {
    if (state !== 'granted') return;
    const version = consentVersion;
    if (!event.product || !event.promise) return;
    const surface = event.target instanceof Element && event.target.closest('quick-add-dialog') ? 'quick_add' : 'product_page';
    const options = (event.selectedOptions || []).map((option) => option.name + ': ' + option.value);
    event.promise
      .then((result) => {
        if (state !== 'granted' || version !== consentVersion) return;
        const variant = (result && result.variant) || null;
        track('variant_selected', {
          surface, product_id: toId(event.product.id), product_handle: event.product.handle, options,
          variant_id: variant ? toId(variant.id) : null, available: variant ? variant.availableForSale : false,
          price: variant ? amount(variant.price && variant.price.amount) : undefined,
        });
      })
      .catch(() => {});
  });
  document.addEventListener('shopify:collection:update', (event) => {
    if (state !== 'granted') return;
    const version = consentVersion;
    if (!event.promise) return;
    const filters = describeFilters(event.productFilters);
    event.promise
      .then((result) => {
        if (state !== 'granted' || version !== consentVersion) return;
        track('collection_filtered', {
          collection_handle: event.collection && event.collection.handle, filters, filters_count: filters.length,
          sort_by: event.sortKey, results_count: result && result.productsCount,
        });
      })
      .catch(() => {});
  });
  document.addEventListener('shopify:search:update', (event) => {
    if (state !== 'granted') return;
    const version = consentVersion;
    if (!event.promise || !event.search) return;
    const predictive = event.target instanceof Element && event.target.closest('predictive-search-component');
    const query = String(event.search.query || '').trim();
    const filters = describeFilters(event.search.productFilters);
    event.promise
      .then((result) => {
        if (state !== 'granted' || version !== consentVersion) return;
        const count = result && result.totalCount;
        if (!predictive) {
          track('search_filtered', { search_term: query, filters, filters_count: filters.length, sort_by: event.search.sortKey, results_count: count });
        } else if (query.length >= 2) {
          settle('search:modal', 'search_suggestions_viewed', { surface: 'modal', search_term: query, results_count: count });
        }
      })
      .catch(() => {});
  });

  // Bubbling phase: components that stop a click (for example after a drag) are not counted.
  document.addEventListener('click', (event) => {
    if (state !== 'granted') return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const declared = target.closest('[data-kunnie-track]');
    if (declared) {
      flush();
      track(declared.dataset.kunnieTrack, dataProperties(declared));
    }
    const result = target.closest('predictive-search-component a[href]');
    if (result) {
      const component = result.closest('predictive-search-component');
      const input = component.querySelector('input[type="search"], input');
      const links = [...component.querySelectorAll('a[href]')];
      flush();
      track('search_result_clicked', {
        surface: 'modal', search_term: input ? input.value.trim() : undefined,
        result_type: resultType(result.href), position: links.indexOf(result) + 1,
      });
    }
    const checkout = target.closest('button[name="checkout"]');
    if (checkout) {
      track('checkout_button_clicked', { surface: checkout.closest('#cart-drawer') ? 'drawer' : 'page', cart_value: cart.value, cart_items_count: cart.items });
    }
    if (target.closest('.accelerated-checkout-block')) track('express_checkout_clicked', { surface: 'product_page' });
  });
  document.addEventListener('submit', (event) => {
    if (state !== 'granted') return;
    const form = event.target;
    if (!(form instanceof Element) || !form.querySelector('input[name="contact[tags]"][value*="newsletter"]')) return;
    track('newsletter_signup_submitted', { source: form.closest('kunnie-newsletter-popup') ? 'popup' : 'footer' });
  });
  document.addEventListener('visitorConsentCollected', evaluate);

  const initial = readPrivacy();
  if (initial) apply(initial.allowed, initial.declined);
  else if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) evaluate();
    });
  }
})();
